import { AxiosResponse } from "axios";

import { OptionNameValuePair } from "../lib/interfaces/types";
import { AreaOfFraud } from "./cases";
import { axiosInstance, MessageResponse } from "./common";
import { Stakeholder } from "./stakeholders";
import { RELATED_AS_LABELS, RelatedAs, StakeholderRelationship } from "./stakeholder_relationships";

export enum AccountType {
  SAVINGS = "SAVINGS",
  CURRENT = "CURRENT",
  TERM_DEPOSIT = "TERM_DEPOSIT",
  RECURRING_DEPOSIT = "RECURRING_DEPOSIT",
  WALLET_PPI = "WALLET_PPI",
}

export enum AccountCategory {
  REGULAR_SAVINGS = "REGULAR_SAVINGS",
  SALARY = "SALARY",
  BSBDA = "BSBDA", // Basic Savings Bank Deposit Account
  SENIOR_CITIZEN_SAVINGS = "SENIOR_CITIZEN_SAVINGS",
  NRE_SAVINGS = "NRE_SAVINGS",
  NRO_SAVINGS = "NRO_SAVINGS",
  REGULAR_CURRENT = "REGULAR_CURRENT",
  OD_LINKED_CURRENT = "OD_LINKED_CURRENT",
  REGULAR_FD = "REGULAR_FD",
  TAX_SAVER_FD = "TAX_SAVER_FD",
  RECURRING_DEPOSIT = "RECURRING_DEPOSIT",
  FULL_KYC_WALLET = "FULL_KYC_WALLET",
  MIN_KYC_WALLET = "MIN_KYC_WALLET",
}

export const ACCOUNT_TYPE_LABELS: Record<AccountType, string> = {
  [AccountType.SAVINGS]: "Savings",
  [AccountType.CURRENT]: "Current",
  [AccountType.TERM_DEPOSIT]: "Term Deposit",
  [AccountType.RECURRING_DEPOSIT]: "Recurring Deposit",
  [AccountType.WALLET_PPI]: "Wallet / PPI",
};

export const ACCOUNT_CATEGORY_LABELS: Record<AccountCategory, string> = {
  [AccountCategory.REGULAR_SAVINGS]: "Regular Savings",
  [AccountCategory.SALARY]: "Salary",
  [AccountCategory.BSBDA]: "BSBDA",
  [AccountCategory.SENIOR_CITIZEN_SAVINGS]: "Senior Citizen Savings",
  [AccountCategory.NRE_SAVINGS]: "NRE Savings",
  [AccountCategory.NRO_SAVINGS]: "NRO Savings",
  [AccountCategory.REGULAR_CURRENT]: "Regular Current",
  [AccountCategory.OD_LINKED_CURRENT]: "OD-Linked Current",
  [AccountCategory.REGULAR_FD]: "Regular FD",
  [AccountCategory.TAX_SAVER_FD]: "Tax Saver FD",
  [AccountCategory.RECURRING_DEPOSIT]: "Recurring Deposit",
  [AccountCategory.FULL_KYC_WALLET]: "Full KYC Wallet",
  [AccountCategory.MIN_KYC_WALLET]: "Min KYC Wallet",
};

export const ACCOUNT_CATEGORIES: Record<AccountType, AccountCategory[]> = {
  [AccountType.SAVINGS]: [
    AccountCategory.REGULAR_SAVINGS,
    AccountCategory.SALARY,
    AccountCategory.BSBDA,
    AccountCategory.SENIOR_CITIZEN_SAVINGS,
    AccountCategory.NRE_SAVINGS,
    AccountCategory.NRO_SAVINGS,
  ],
  [AccountType.CURRENT]: [
    AccountCategory.REGULAR_CURRENT,
    AccountCategory.OD_LINKED_CURRENT,
  ],
  [AccountType.TERM_DEPOSIT]: [
    AccountCategory.REGULAR_FD,
    AccountCategory.TAX_SAVER_FD,
  ],
  [AccountType.RECURRING_DEPOSIT]: [AccountCategory.RECURRING_DEPOSIT],
  [AccountType.WALLET_PPI]: [
    AccountCategory.FULL_KYC_WALLET,
    AccountCategory.MIN_KYC_WALLET,
  ],
};

export const ACCOUNT_TYPE_OPTIONS: OptionNameValuePair<string>[] = Object.values(
  AccountType,
).map((value) => ({ name: ACCOUNT_TYPE_LABELS[value], value }));

export const buildAccountCategoryOptions = (
  accountType: AccountType | "",
): OptionNameValuePair<string>[] => {
  if (!accountType) return [];
  return (ACCOUNT_CATEGORIES[accountType] ?? []).map((value) => ({
    name: ACCOUNT_CATEGORY_LABELS[value],
    value,
  }));
};

export const OPERATIONAL_PARTY_ROLES: RelatedAs[] = [
  RelatedAs.PRIMARY_ACCOUNT_HOLDER,
  RelatedAs.JOINT_ACCOUNT_HOLDER,
  RelatedAs.BENEFICIARY_ACCOUNT_HOLDER,
  RelatedAs.NOMINEE,
  RelatedAs.MANDATE_HOLDER,
  RelatedAs.POWER_OF_ATTORNEY_HOLDER,
  RelatedAs.EMPLOYEE,
  RelatedAs.FORMER_EMPLOYEE,
  RelatedAs.RELATIONSHIP_MANAGER,
  RelatedAs.SALES_MANAGER,
  RelatedAs.BRANCH_MANAGER,
  RelatedAs.KYC_OFFICER,
  RelatedAs.CUSTOMER_SERVICE_OFFICER,
  RelatedAs.TELLER,
  RelatedAs.BUSINESS_CORRESPONDENT,
  RelatedAs.BUSINESS_CORRESPONDENT_AGENT,
  RelatedAs.DRAWER,
  RelatedAs.PAYEE,
  RelatedAs.ATM_CUSTODIAN,
  RelatedAs.CASH_REPLENISHMENT_AGENCY,
  RelatedAs.CHEST_CUSTODIAN,
  RelatedAs.HEAD_CASHIER,
  RelatedAs.VENDOR,
  RelatedAs.PROPRIETOR,
  RelatedAs.PARTNER,
  RelatedAs.DIRECTOR,
  RelatedAs.PROMOTER,
  RelatedAs.TRUSTEE,
  RelatedAs.AUTHORIZED_SIGNATORY,
  RelatedAs.OTHER,
  RelatedAs.OTHERS_EMPLOYEE,
  RelatedAs.OTHERS_OUTSIDER,
];

export const CYBER_PARTY_ROLES: RelatedAs[] = [
  RelatedAs.PRIMARY_ACCOUNT_HOLDER,
  RelatedAs.JOINT_ACCOUNT_HOLDER,
  RelatedAs.BENEFICIARY_ACCOUNT_HOLDER,
  RelatedAs.IT_STAFF,
  RelatedAs.SYSTEM_ADMIN,
  RelatedAs.THIRD_PARTY_FRAUDSTER,
  RelatedAs.EXTERNAL_ATTACKER,
  RelatedAs.EMPLOYEE,
  RelatedAs.FORMER_EMPLOYEE,
  RelatedAs.RELATIONSHIP_MANAGER,
  RelatedAs.BRANCH_MANAGER,
  RelatedAs.VENDOR,
  RelatedAs.PROPRIETOR,
  RelatedAs.PARTNER,
  RelatedAs.DIRECTOR,
  RelatedAs.PROMOTER,
  RelatedAs.TRUSTEE,
  RelatedAs.AUTHORIZED_SIGNATORY,
  RelatedAs.OTHER,
  RelatedAs.OTHERS_EMPLOYEE,
  RelatedAs.OTHERS_OUTSIDER,
];

export const OPERATIONAL_EMPLOYEE_ROLES: RelatedAs[] = [
  RelatedAs.EMPLOYEE,
  RelatedAs.FORMER_EMPLOYEE,
  RelatedAs.RELATIONSHIP_MANAGER,
  RelatedAs.SALES_MANAGER,
  RelatedAs.BRANCH_MANAGER,
  RelatedAs.KYC_OFFICER,
  RelatedAs.CUSTOMER_SERVICE_OFFICER,
  RelatedAs.TELLER,
  RelatedAs.CHEST_CUSTODIAN,
  RelatedAs.HEAD_CASHIER,
  RelatedAs.OTHERS_EMPLOYEE,
];

export const CYBER_EMPLOYEE_ROLES: RelatedAs[] = [
  RelatedAs.IT_STAFF,
  RelatedAs.SYSTEM_ADMIN,
  RelatedAs.EMPLOYEE,
  RelatedAs.FORMER_EMPLOYEE,
  RelatedAs.RELATIONSHIP_MANAGER,
  RelatedAs.BRANCH_MANAGER,
  RelatedAs.OTHERS_EMPLOYEE,
];

export const isEmployeeRole = (role: RelatedAs, areaOfFraud: AreaOfFraud): boolean => {
  if (areaOfFraud === AreaOfFraud.OPERATIONAL) {
    return OPERATIONAL_EMPLOYEE_ROLES.includes(role);
  }
  if (areaOfFraud === AreaOfFraud.CYBER) {
    return CYBER_EMPLOYEE_ROLES.includes(role);
  }
  return false;
};

/** Draft / API account row for Cyber & Operational fraud. */
export interface Account {
  account_number: string;
  ext_account_number: string;
  account_name: string;
  account_type: AccountType | "";
  account_category: AccountCategory | "";
}

export interface CreateAccountData {
  account_number: string;
  ext_account_number: string;
  account_name: string;
  account_type: AccountType;
  account_category: AccountCategory;
  primary_account_holder_stakeholder_id?: string;
}

interface CreateAccountsRequest {
  accounts: CreateAccountData[];
  stakeholders?: Stakeholder[];
}

export interface CreateAccountsResponse {
  accounts: Account[];
  stakeholders?: Stakeholder[];
  stakeholder_relationships?: StakeholderRelationship[];
}

export interface GetAccountsResponse {
  accounts: Account[];
}

export interface UpdateAccountRequest {
  account_name?: string;
  account_type?: AccountType;
  account_category?: AccountCategory;
  ext_account_number?: string;
}

export const AccountsAPI = {
  add: async (
    case_id: string,
    accounts: CreateAccountData[],
    stakeholders?: Stakeholder[],
  ) => {
    const request: CreateAccountsRequest = { accounts, stakeholders };
    const response: AxiosResponse<CreateAccountsResponse> =
      await axiosInstance.post(`/v1/cases/${case_id}/accounts`, request);
    return response.data;
  },
  get: async (case_id: string) => {
    const response: AxiosResponse<GetAccountsResponse> =
      await axiosInstance.get(`/v1/cases/${case_id}/accounts`);
    return response.data;
  },
  update: async (
    case_id: string,
    account_number: string,
    request: UpdateAccountRequest,
  ) => {
    const response: AxiosResponse<MessageResponse> = await axiosInstance.patch(
      `/v1/cases/${case_id}/accounts/${account_number}`,
      request,
    );
    return response.data;
  },
};
